import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Link, Navigate, useParams, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { RouteTitle } from './components/RouteTitle';
import { AnonymousClaimBanner } from './components/AnonymousClaimBanner';
import { WechatBrowserGuard } from './components/WechatBrowserGuard';
import { useAttributeAnonymous } from './hooks/useAttributeAnonymous';
import { useVisitorPing } from './hooks/useVisitorPing';
import { useTheme } from './contexts/ThemeContext';
import { immersiveConfig, IMMERSIVE_STATUS_COLOR } from './lib/immersive';

/**
 * 路由切换（pathname 变化）后重置滚动到顶端。
 * 只看 pathname，不看 search —— /datasets?cat= 同页切换分类不强制回顶。
 * 解决「点进新页面却停在上一页滚动位置」的问题。
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

/**
 * iOS 状态栏/地址栏配色（meta[name=theme-color]）的唯一管理处，按「路由 + 主题 + 滚动」决定：
 *   普通页 → 主题色（白 / #0d1117），与页面底色同色 → 内容延伸到状态栏下无缝；
 *   沉浸页（首页 / 数据来源）hero 在顶端时 → hero 深色 #050f24，使顶条与深色 hero 同色沉浸；
 *   沉浸页滚过 hero(按 statusThresholdVh) → 恢复主题色，避免深顶条压在浅色正文上。
 * 这是「首页/数据来源没实现沉浸、其它页正常」的真正修复：状态栏顶条颜色由 theme-color 决定，
 * 之前沉浸页仍是白顶条压深 hero。
 */
function StatusBarColor() {
  const { resolved } = useTheme();
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const themeDefault = resolved === 'dark' ? '#0d1117' : '#ffffff';
    const cfg = immersiveConfig(pathname);
    let last = '';
    const set = (c: string) => {
      if (c !== last) {
        meta.setAttribute('content', c);
        last = c;
      }
    };
    if (!cfg) {
      set(themeDefault);
      return;
    }
    let raf = 0;
    const update = () => {
      raf = 0;
      const threshold = window.innerHeight * cfg.statusThresholdVh;
      set(window.scrollY < threshold ? IMMERSIVE_STATUS_COLOR : themeDefault);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pathname, resolved]);
  return null;
}

const Home = lazy(() => import('./pages/Home'));
const Datasets = lazy(() => import('./pages/Datasets'));
const DatasetDetail = lazy(() => import('./pages/DatasetDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const ProfileAccount = lazy(() => import('./pages/Profile').then((m) => ({ default: m.ProfileAccount })));
const MyDatasets = lazy(() => import('./pages/MyDatasets'));
const MyDownloads = lazy(() => import('./pages/MyDownloads'));
const Redeem = lazy(() => import('./pages/Redeem'));
const Pricing = lazy(() => import('./pages/Pricing'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const About = lazy(() => import('./pages/About'));
const DataSources = lazy(() => import('./pages/DataSources'));
const Faq = lazy(() => import('./pages/Faq'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Search = lazy(() => import('./pages/Search'));
const ScanLogin = lazy(() => import('./pages/ScanLogin'));

const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminDatasets = lazy(() => import('./pages/admin/AdminDatasets'));
const AdminLinks = lazy(() => import('./pages/admin/AdminLinks'));
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'));
const AdminRedemption = lazy(() => import('./pages/admin/AdminRedemption'));
const AdminRisk = lazy(() => import('./pages/admin/AdminRisk')); // 风控溯源面板
const AdminUserGroups = lazy(() => import('./pages/admin/AdminUserGroups'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminAuditLogs = lazy(() => import('./pages/admin/AdminAuditLogs'));

function Fallback() {
  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-6 py-16 text-fg-subtle">加载中…</div>
    </Layout>
  );
}

/**
 * 兼容旧 /library/:code 链接：跳到 /datasets?cat=<code>。
 * level 用 catch-all "category"，Datasets 内部不会因 level 不准而出错（categoryCode 是顶级 root 时正常工作；
 * 其它层级时后端会按 categoryCode 过滤 → 命中 0 条；这是历史兼容路径，预期访问量很低）。
 */
function LibraryRedirect() {
  const params = useParams();
  const code = params.code;
  if (!code) return <Navigate to="/datasets" replace />;
  return <Navigate to={`/datasets?cat=${encodeURIComponent(code)}`} replace />;
}

function NotFound() {
  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <h1 className="text-3xl font-semibold text-fg-muted">404</h1>
        <p className="mt-2 text-fg-subtle">页面不存在</p>
        <Link className="mt-4 inline-block text-navy underline" to="/">
          返回首页
        </Link>
      </div>
    </Layout>
  );
}

export default function App() {
  useAttributeAnonymous();
  useVisitorPing();
  return (
    <Suspense fallback={<Fallback />}>
      <WechatBrowserGuard />
      <AnonymousClaimBanner />
      <RouteTitle />
      <ScrollToTop />
      <StatusBarColor />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/about" element={<About />} />
        <Route path="/data-sources" element={<DataSources />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        {/* /library 已合并到 /datasets — 旧链接重定向（带 code 时按需选中分类） */}
        <Route path="/library" element={<Navigate to="/datasets" replace />} />
        <Route path="/library/:code" element={<LibraryRedirect />} />
        <Route path="/search" element={<Search />} />
        <Route path="/scan-login" element={<ScanLogin />} />
        <Route path="/datasets" element={<Datasets />} />
        <Route path="/datasets/:code" element={<DatasetDetail />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/redeem" element={<Redeem />} />
        <Route path="/profile" element={<Profile />}>
          <Route index element={<ProfileAccount />} />
          <Route path="datasets" element={<MyDatasets />} />
          <Route path="downloads" element={<MyDownloads />} />
        </Route>

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="datasets" element={<AdminDatasets />} />
          <Route path="links" element={<AdminLinks />} />
          <Route path="redemption-codes" element={<AdminRedemption />} />
          <Route path="risk" element={<AdminRisk />} />
          <Route path="user-groups" element={<AdminUserGroups />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="audit-logs" element={<AdminAuditLogs />} />
          <Route path="jobs" element={<AdminJobs />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
